import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AttachFile as AttachFileIcon,
  CheckCircle as CheckCircleIcon,
  ErrorOutline as ErrorOutlineIcon,
  Search as SearchIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { Modal } from '../../../common/components/Modal';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import EnhancedMarkdownEditor from './EnhancedMarkdownEditor';
import { MarkdownAttachment, MarkdownImage, MarkdownVideo } from '../services/markdownService';
import { hasDangerousContent, sanitizeMarkdownContent } from '../utils/contentSanitizer';
import { ModuleUrls, ModuleNames } from '../../../common/constants/userConstants';
import { getModuleRouteSuffixes } from '../utils/routeExtractor';
import TestIdPickerDialog, { ScannedElement } from './TestIdPickerDialog';

interface HelpContent {
  id: string;
  moduleId: string;
  title: string;
  content: string;
  contentType: 'markdown' | 'video';
  videoUrl?: string;
  createdAt: string;
  updatedAt: string;
  dataTestId?: string;
  pageUrl?: string;
  attachmentList?: MarkdownAttachment[];
}

interface HelpEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  content: HelpContent | null;
  onSave: (content: HelpContent) => void;
}

const HelpEditModal: React.FC<HelpEditModalProps> = ({ isOpen, onClose, content, onSave }) => {
  const { t } = useTranslation();

  // Modules whose pages are TOP-LEVEL routes that do NOT share a single base
  // path (e.g. UPU TD Accounting's "/cn54bis-cn56-summary", "/no3-cn56-summary",
  // "/cn61-crr"). Forcing a "/td-accounting" prefix here would save the wrong
  // pageUrl and break contextual help (which matches the raw route), so these
  // modules never get a prefix in the Help modal — the full route is entered/
  // stored as-is. (ModuleUrls itself is left intact; permissionConstants relies
  // on it.)
  const PREFIXLESS_MODULES: string[] = [
    ModuleNames.IPC_TD_ACCOUNTING,
    ModuleNames.TD_ACCOUNTING,
    ModuleNames.SAMPLING_PLANNING,
  ];

  const configuredPrefix =
    content?.moduleId && ModuleUrls[content.moduleId as keyof typeof ModuleUrls]
      ? ModuleUrls[content.moduleId as keyof typeof ModuleUrls]
      : '';

  // No prefix for prefix-less modules, or when an existing entry's pageUrl
  // already lives outside the module base (so saving won't corrupt it).
  const moduleUrlPrefix =
    !configuredPrefix ||
    (content?.moduleId && PREFIXLESS_MODULES.includes(content.moduleId)) ||
    (content?.pageUrl && !content.pageUrl.startsWith(configuredPrefix))
      ? ''
      : configuredPrefix;

  /**
   * Strip the module prefix from a full pageUrl so we only store the suffix
   * in formData (e.g. "/eno1/samples" → "/samples").
   */
  const stripPrefix = (url: string): string => {
    if (moduleUrlPrefix && url.startsWith(moduleUrlPrefix)) {
      const suffix = url.slice(moduleUrlPrefix.length);
      return suffix || '/';
    }
    return url;
  };

  /**
   * Build the full URL that will be sent to the API: prefix + suffix.
   * When suffix is empty (or "/"), and a moduleUrlPrefix exists, the full URL
   * is just the module root (e.g. "/eno1").
   */
  const buildFullPageUrl = (suffix: string): string => {
    if (!moduleUrlPrefix) return suffix;
    // Empty or bare "/" suffix → the content lives at the module root
    if (!suffix || suffix === '/') return moduleUrlPrefix;
    // Avoid double slashes: prefix already starts with "/", suffix should start with "/"
    if (suffix.startsWith('/')) {
      return `${moduleUrlPrefix}${suffix}`;
    }
    return `${moduleUrlPrefix}/${suffix}`;
  };

  // Autocomplete options: known route suffixes for this module
  const routeSuffixOptions = useMemo(
    () => getModuleRouteSuffixes(moduleUrlPrefix),
    [moduleUrlPrefix]
  );

  const [formData, setFormData] = useState<Partial<HelpContent>>({
    title: '',
    content: `# Welcome to Help Documentation

This is a comprehensive guide that includes various content types.

## Video Tutorial

Below is an embedded video tutorial:

<iframe 
  width="100%" 
  height="300" 
  src="https://www.youtube.com/embed/6Ui3bg35QRE" 
  title="YouTube video player" 
  frameborder="0" 
  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
  allowfullscreen>
</iframe>

## Additional Content

You can also include:
- **Bold text** for emphasis
- *Italic text* for subtle emphasis
- \`code snippets\` for technical content
- [Links](https://example.com) to external resources

### Code Example

\`\`\`javascript
// Example JavaScript code
function greetUser(name) {
  return \`Hello, \${name}!\`;
}
\`\`\`

> **Note**: This markdown supports HTML video tags for embedding videos directly in the content.

---
*Last updated: ${new Date().toLocaleDateString()}*`,
    contentType: 'markdown',
    dataTestId: '',
    pageUrl: '',
  });

  const [uploadedFiles, setUploadedFiles] = useState<
    Array<{ originalFile: File; uploadedUrl: string }>
  >([]);

  const [uploadedAttachments, setUploadedAttachments] = useState<
    Array<{ originalFile: File; uploadedUrl: string }>
  >([]);

  // Data Test ID scanning
  const [scannedTestIds, setScannedTestIds] = useState<string[]>([]);
  const [scannedElements, setScannedElements] = useState<ScannedElement[]>([]);
  const [isScanning, setIsScanning] = useState(false);
  const [scanStatus, setScanStatus] = useState<'idle' | 'success' | 'error' | 'no-url' | 'needs-id'>('idle');
  const [isPickerOpen, setIsPickerOpen] = useState(false);

  /**
   * Scan an iframe at the given URL and extract data-testid attributes.
   * Returns a promise that resolves with the found IDs.
   */
  const extractTestIdsFromUrl = useCallback((url: string): Promise<string[]> => {
    return new Promise((resolve) => {
      const iframe = document.createElement('iframe');
      iframe.style.cssText =
        'position:fixed;top:-10000px;left:-10000px;width:1024px;height:768px;opacity:0;pointer-events:none;';
      iframe.src = url;

      const cleanup = () => {
        try {
          document.body.removeChild(iframe);
        } catch {
          /* already removed */
        }
      };

      const timeout = setTimeout(() => {
        cleanup();
        resolve([]);
      }, 15000);

      iframe.onload = () => {
        clearTimeout(timeout);
        try {
          const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
          if (!iframeDoc) {
            cleanup();
            resolve([]);
            return;
          }
          // Wait for React to hydrate / render
          setTimeout(() => {
            try {
              const elements = iframeDoc.querySelectorAll('[data-testid]');
              const ids = Array.from(elements)
                .map((el) => el.getAttribute('data-testid') || '')
                .filter(Boolean);
              resolve([...new Set(ids)].sort());
            } catch {
              resolve([]);
            } finally {
              cleanup();
            }
          }, 2000);
        } catch {
          cleanup();
          resolve([]);
        }
      };

      iframe.onerror = () => {
        clearTimeout(timeout);
        cleanup();
        resolve([]);
      };

      document.body.appendChild(iframe);
    });
  }, []);

  /**
   * Load the Page URL in a hidden iframe and extract all data-testid attributes.
   * If the exact URL yields no results, falls back to the module root.
   */
  const scanPageForTestIds = useCallback(async () => {
    const pageUrl = buildFullPageUrl(formData.pageUrl || '');
    if (!pageUrl || pageUrl === moduleUrlPrefix) {
      // If there's no suffix, scan the module root directly
      if (moduleUrlPrefix) {
        setIsScanning(true);
        setScanStatus('idle');
        setScannedTestIds([]);
        const ids = await extractTestIdsFromUrl(moduleUrlPrefix);
        setScannedTestIds(ids);
        setScanStatus(ids.length > 0 ? 'success' : 'error');
        setIsScanning(false);
        return;
      }
      setScanStatus('no-url');
      return;
    }

    setIsScanning(true);
    setScanStatus('idle');
    setScannedTestIds([]);

    // 1. Try scanning the exact URL
    let ids = await extractTestIdsFromUrl(pageUrl);

    // 2. If no results and the URL differs from the module root, fall back to the module root
    if (ids.length === 0 && moduleUrlPrefix && pageUrl !== moduleUrlPrefix) {
      ids = await extractTestIdsFromUrl(moduleUrlPrefix);
      if (ids.length > 0) {
        setScanStatus('needs-id');
      } else {
        setScanStatus('error');
      }
    } else {
      setScanStatus(ids.length > 0 ? 'success' : 'error');
    }

    setScannedTestIds(ids);
    setIsScanning(false);
  }, [formData.pageUrl, moduleUrlPrefix, extractTestIdsFromUrl]);

  /** Whether the currently entered dataTestId exists in the scanned list */
  const testIdValidated =
    scannedTestIds.length > 0 && scannedTestIds.includes(formData.dataTestId || '');
  const testIdNotFound =
    scannedTestIds.length > 0 &&
    (formData.dataTestId || '').length > 0 &&
    !scannedTestIds.includes(formData.dataTestId || '');

  useEffect(() => {
    if (content) {
      const formData = {
        title: content.title,
        content: content.content,
        contentType: content.contentType,
        dataTestId: content.dataTestId || '',
        pageUrl: stripPrefix(content.pageUrl || ''),
      };
      setFormData(formData);
    } else {
      setFormData({
        title: '',
        content: '',
        contentType: 'markdown',
        dataTestId: '',
        pageUrl: '',
      });
    }
    // Reset scan state when content changes
    setScannedTestIds([]);
    setScannedElements([]);
    setScanStatus('idle');
  }, [content, moduleUrlPrefix]);

  const handleInputChange = (field: keyof HelpContent, value: string) => {
    let normalizedValue = value;
    if (field === 'pageUrl' && normalizedValue !== '' && !normalizedValue.startsWith('/')) {
      normalizedValue = `/${normalizedValue}`;
    }
    // Prevent the user from accidentally typing the module prefix again
    if (field === 'pageUrl' && moduleUrlPrefix && normalizedValue.startsWith(moduleUrlPrefix)) {
      normalizedValue = normalizedValue.slice(moduleUrlPrefix.length) || '/';
    }
    setFormData((prev) => ({
      ...prev,
      [field]: normalizedValue,
    }));
  };

  const handleSave = () => {
    // pageUrl is optional when moduleUrlPrefix covers the root route
    const pageUrlProvided = !!formData.pageUrl || !!moduleUrlPrefix;
    if (!formData.title || !formData.content || !pageUrlProvided) {
      return;
    }

    // Sanitize content before saving to remove any dangerous HTML/scripts
    const sanitizedContent = sanitizeMarkdownContent(formData.content || '');

    // Convert uploaded files to imageList and videoList
    const imageList: MarkdownImage[] = uploadedFiles
      .filter((file) => file.originalFile.type.startsWith('image/'))
      .map((file) => ({
        url: file.uploadedUrl,
        imagename: file.originalFile.name,
        included: true,
      }));

    const videoList: MarkdownVideo[] = uploadedFiles
      .filter((file) => file.originalFile.type.startsWith('video/'))
      .map((file) => ({
        url: file.uploadedUrl,
        videoname: file.originalFile.name,
        included: true,
      }));

    const attachmentList: MarkdownAttachment[] = uploadedAttachments.map((file) => ({
      url: file.uploadedUrl,
      filename: file.originalFile.name,
      included: true,
    }));

    const updatedContent: HelpContent = {
      id: content?.id || Date.now().toString(),
      moduleId: content?.moduleId || '',
      title: formData.title,
      content: sanitizedContent,
      contentType: formData.contentType as 'markdown' | 'video',
      createdAt: content?.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      dataTestId: formData.dataTestId,
      pageUrl: buildFullPageUrl(formData.pageUrl || ''),
    };

    // Pass the imageList, videoList, and attachmentList to the parent component
    onSave({
      ...updatedContent,
      imageList,
      videoList,
      attachmentList,
    } as any);
  };

  const isValid = formData.title && formData.content && (formData.pageUrl || moduleUrlPrefix) && formData.dataTestId;
  const hasDangerous = formData.content ? hasDangerousContent(formData.content) : false;

  // Get validation messages
  const getValidationMessages = (): string[] => {
    const messages: string[] = [];
    if (!formData.title) {
      messages.push(t('help.validation.titleRequired', 'Title is required'));
    }
    if (!formData.content) {
      messages.push(t('help.validation.contentRequired', 'Content is required'));
    }
    if (!formData.pageUrl && !moduleUrlPrefix) {
      messages.push(t('help.validation.pageUrlRequired', 'Page URL is required'));
    }
    if (!formData.dataTestId) {
      messages.push(t('help.validation.dataTestIdRequired', 'Data Test ID is required'));
    }
    if (hasDangerous) {
      messages.push(
        t(
          'help.validation.dangerousContent',
          'Content contains unsafe elements that will be removed'
        )
      );
    }
    return messages;
  };

  const validationMessages = getValidationMessages();

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={
        content
          ? t('help.editContent', 'Edit Help Content')
          : t('help.addContent', 'Add Help Content')
      }
      maxWidth='md'
      PaperProps={{
        sx: {
          maxHeight: '90vh',
          height: 'auto',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          margin: 'auto',
          '& .MuiDialogContent-root': {
            overflow: 'auto',
            flex: 1,
          },
        },
      }}
      scroll='paper'
      disableScrollLock={true}
      disableEscapeKeyDown={false}
      onApply={handleSave}
      applyText={t('common.save', 'Save')}
      cancelText={t('common.cancel', 'Cancel')}
      disableSave={!isValid || hasDangerous}
      data-testid='help-edit-modal'
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          maxHeight: 'calc(90vh - 200px)',
          padding: 1,
        }}
        data-testid='help-edit-modal-content'
      >
        {/* Validation Messages */}
        {validationMessages.length > 0 && (
          <Box sx={{ mb: 1 }}>
            {validationMessages.map((message, index) => (
              <Typography
                key={index}
                variant='body2'
                color={
                  hasDangerous && index === validationMessages.length - 1
                    ? 'error'
                    : 'text.secondary'
                }
                sx={{
                  mb: 0.5,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                }}
              >
                <span>•</span> {message}
              </Typography>
            ))}
          </Box>
        )}
        {/* Title */}
        <TextField
          label={t('help.contentTitle', 'Content Title')}
          value={formData.title}
          onChange={(e) => handleInputChange('title', e.target.value)}
          fullWidth
          required
          data-testid='help-edit-title-field'
        />

        {/* Page URL – Autocomplete with known routes for the module */}
        <Autocomplete
          freeSolo
          fullWidth
          options={routeSuffixOptions}
          value={formData.pageUrl || ''}
          onInputChange={(_e, value) => handleInputChange('pageUrl', value)}
          onChange={(_e, value) => handleInputChange('pageUrl', value || '')}
          filterOptions={(options, { inputValue }) => {
            // Filter options that match the typed input (case-insensitive)
            const needle = inputValue.toLowerCase();
            return options.filter((opt) => opt.toLowerCase().includes(needle));
          }}
          renderOption={(props, option) => (
            <li {...props}>
              <Typography
                variant='body2'
                sx={{ fontFamily: 'monospace' }}
              >
                <Typography
                  component='span'
                  variant='body2'
                  color='primary'
                  sx={{ fontFamily: 'monospace', fontWeight: 600 }}
                >
                  {moduleUrlPrefix}
                </Typography>
                {option}
              </Typography>
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('help.pageUrl', 'Page URL')}
              required
              helperText={
                moduleUrlPrefix
                  ? routeSuffixOptions.length > 0
                    ? t(
                        'help.pageUrlHelperWithRoutes',
                        'The module prefix "{{prefix}}" is added automatically. Select a known route or type a custom path.',
                        { prefix: moduleUrlPrefix }
                      )
                    : t(
                        'help.pageUrlHelperWithPrefix',
                        'The module prefix "{{prefix}}" is added automatically. Enter the rest of the path (e.g. /samples).',
                        { prefix: moduleUrlPrefix }
                      )
                  : t(
                      'help.pageUrlHelper',
                      'Enter the page URL where this help content will be displayed'
                    )
              }
              InputProps={{
                ...params.InputProps,
                startAdornment: moduleUrlPrefix ? (
                  <>
                    <InputAdornment position='start'>
                      <Chip
                        label={moduleUrlPrefix}
                        size='small'
                        color='primary'
                        variant='outlined'
                        sx={{ fontFamily: 'monospace', fontWeight: 600 }}
                      />
                    </InputAdornment>
                    {params.InputProps.startAdornment}
                  </>
                ) : (
                  params.InputProps.startAdornment
                ),
              }}
              data-testid='help-edit-page-url-field'
            />
          )}
          data-testid='help-edit-page-url-autocomplete'
        />

        {/* Data Test ID – Autocomplete with page scanning + visual picker */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
          <Autocomplete
            freeSolo
            fullWidth
            options={scannedTestIds}
            value={formData.dataTestId || ''}
            onInputChange={(_e, value) => handleInputChange('dataTestId', value)}
            onChange={(_e, value) => handleInputChange('dataTestId', value || '')}
            renderOption={(props, option) => {
              const meta = scannedElements.find((el) => el.testId === option);
              return (
                <li {...props}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip
                        label={option}
                        size='small'
                        variant='outlined'
                        sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}
                      />
                      {meta && (
                        <Chip
                          label={`<${meta.tagName}>`}
                          size='small'
                          color='default'
                          sx={{ fontFamily: 'monospace', fontSize: '0.7rem', height: 20 }}
                        />
                      )}
                    </Box>
                    {meta?.textPreview && (
                      <Typography
                        variant='caption'
                        color='text.secondary'
                        noWrap
                        sx={{ mt: 0.25, pl: 0.5, maxWidth: '100%' }}
                      >
                        {meta.textPreview}
                      </Typography>
                    )}
                  </Box>
                </li>
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('help.dataTestId', 'Data Test ID')}
                required
                helperText={
                  scanStatus === 'success'
                    ? t(
                        'help.dataTestIdScanned',
                        '{{count}} data-testid attribute(s) found on the page. Select one or type a custom value.',
                        { count: scannedTestIds.length }
                      )
                    : scanStatus === 'needs-id'
                      ? t(
                          'help.dataTestIdNeedsId',
                          '{{count}} data-testid attribute(s) found on the module root (the exact URL may require a real ID to load). Select one or type a custom value.',
                          { count: scannedTestIds.length }
                        )
                      : scanStatus === 'error'
                        ? t(
                            'help.dataTestIdScanError',
                            'Could not scan the page. You can still type a value manually.'
                          )
                        : scanStatus === 'no-url'
                          ? t(
                              'help.dataTestIdNoUrl',
                              'Please enter a Page URL first, then scan.'
                            )
                          : t(
                              'help.dataTestIdHelper',
                              'Enter a unique identifier for testing purposes. Use the scan button to extract IDs from the page.'
                            )
                }
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <InputAdornment position='end'>
                      {testIdValidated && (
                        <Tooltip title={t('help.dataTestIdFound', 'This data-testid exists on the page')}>
                          <CheckCircleIcon color='success' fontSize='small' />
                        </Tooltip>
                      )}
                      {testIdNotFound && (
                        <Tooltip title={t('help.dataTestIdNotFound', 'This data-testid was NOT found on the page')}>
                          <ErrorOutlineIcon color='warning' fontSize='small' />
                        </Tooltip>
                      )}
                      {params.InputProps.endAdornment}
                    </InputAdornment>
                  ),
                }}
                data-testid='help-edit-data-test-id-field'
              />
            )}
            data-testid='help-edit-data-test-id-autocomplete'
          />
          {/* Scan (list) button */}
          <Tooltip
            title={
              isScanning
                ? t('help.scanning', 'Scanning page…')
                : t('help.scanPage', 'Scan page for data-testid attributes')
            }
          >
            <span>
              <IconButton
                onClick={scanPageForTestIds}
                disabled={isScanning}
                color='primary'
                sx={{ mt: 1 }}
                data-testid='help-edit-scan-testids-btn'
              >
                {isScanning ? <CircularProgress size={20} /> : <SearchIcon />}
              </IconButton>
            </span>
          </Tooltip>
          {/* Visual picker button */}
          <Tooltip
            title={t('help.pickFromPage', 'Pick data-testid visually from the rendered page')}
          >
            <span>
              <IconButton
                onClick={() => setIsPickerOpen(true)}
                disabled={!formData.pageUrl && !moduleUrlPrefix}
                color='secondary'
                sx={{ mt: 1 }}
                data-testid='help-edit-visual-picker-btn'
              >
                <VisibilityIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Box>

        {/* Visual Test ID Picker Dialog */}
        <TestIdPickerDialog
          open={isPickerOpen}
          onClose={() => setIsPickerOpen(false)}
          pageUrl={buildFullPageUrl(formData.pageUrl || '') || moduleUrlPrefix}
          onSelect={(testId) => {
            handleInputChange('dataTestId', testId);
            setScanStatus('success');
          }}
          onElementsFound={(elements) => {
            setScannedElements(elements);
            const ids = elements.map((el) => el.testId);
            setScannedTestIds(ids);
            if (ids.length > 0) setScanStatus('success');
          }}
        />

        {/* Content */}
        <EnhancedMarkdownEditor
          value={formData.content || ''}
          onChange={(value) => handleInputChange('content', value)}
          label={t('help.markdownContent', 'Markdown Content')}
          helperText={t(
            'help.markdownHelper',
            'You can use markdown syntax. You can also embed videos using HTML video tags.'
          )}
          required
          dataTestId='help-edit-markdown-field'
          onSave={() => {
            // This will be called when user uploads files
          }}
          onFilesChange={(files) => {
            setUploadedFiles(files);
          }}
          onAttachmentsChange={(attachments) => {
            setUploadedAttachments(attachments);
          }}
        />

        {/* Uploaded Attachments */}
        {uploadedAttachments.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.5 }}>
            <Typography variant='caption' color='text.secondary' sx={{ width: '100%' }}>
              {t('help.attachmentCount', '{{count}} attachment(s)', { count: uploadedAttachments.length })}
            </Typography>
            {uploadedAttachments.map((attachment, index) => (
              <Chip
                key={index}
                icon={<AttachFileIcon />}
                label={attachment.originalFile.name}
                size='small'
                variant='outlined'
                color='primary'
              />
            ))}
          </Box>
        )}

        {/* Preview - Only show if content is safe */}
        {formData.content && !hasDangerous && (
          <Box data-testid='help-edit-preview-section'>
            <Typography variant='h4' color='text.secondary' gutterBottom>
              {t('help.preview', 'Preview:')}
            </Typography>
            <Box
              data-testid='help-edit-preview-content'
              sx={{
                border: '1px solid #ddd',
                borderRadius: 1,
                p: 2,
                minHeight: '200px',
                backgroundColor: '#f9f9f9',
                '& h1, & h2, & h3, & h4, & h5, & h6': {
                  marginTop: 1,
                  marginBottom: 0.5,
                  fontWeight: 600,
                  color: '#063142',
                },
                '& h1': {
                  fontSize: '1.25rem',
                  borderBottom: '2px solid #E1E8ED',
                  paddingBottom: '0.25rem',
                },
                '& h2': {
                  fontSize: '1.1rem',
                  borderBottom: '1px solid #E1E8ED',
                  paddingBottom: '0.125rem',
                },
                '& h3': {
                  fontSize: '1rem',
                },
                '& p': {
                  marginBottom: 0.5,
                  lineHeight: 1.4,
                },
                '& ul, & ol': {
                  marginBottom: 0.5,
                  paddingLeft: 1.5,
                },
                '& li': {
                  marginBottom: '0.125rem',
                },
                '& code': {
                  backgroundColor: '#f5f5f5',
                  padding: '0.125rem 0.25rem',
                  borderRadius: '0.25rem',
                  fontSize: '0.875rem',
                  fontFamily: 'monospace',
                },
                '& pre': {
                  backgroundColor: '#f8f9fa',
                  padding: '0.5rem',
                  borderRadius: '0.25rem',
                  overflowX: 'auto',
                  border: '1px solid #e9ecef',
                  marginBottom: 0.5,
                },
                '& pre code': {
                  backgroundColor: 'transparent',
                  padding: 0,
                },
                '& blockquote': {
                  borderLeft: '3px solid #00B6BA',
                  paddingLeft: '0.75rem',
                  marginLeft: 0,
                  fontStyle: 'italic',
                  color: '#666',
                },
                '& table': {
                  borderCollapse: 'collapse',
                  width: '100%',
                  marginBottom: 0.5,
                },
                '& th, & td': {
                  border: '1px solid #ddd',
                  padding: '0.25rem',
                  textAlign: 'left',
                  fontSize: '0.875rem',
                },
                '& th': {
                  backgroundColor: '#f8f9fa',
                  fontWeight: 600,
                },
                '& a': {
                  color: '#00B6BA',
                  textDecoration: 'none',
                  '&:hover': {
                    textDecoration: 'underline',
                  },
                },
                '& strong': {
                  fontWeight: 600,
                },
                '& em': {
                  fontStyle: 'italic',
                },
                '& img': {
                  maxWidth: '100%',
                  height: 'auto',
                  borderRadius: '4px',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                },
                '& video': {
                  maxWidth: '100%',
                  height: 'auto',
                  borderRadius: '4px',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                },
              }}
            >
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                rehypePlugins={[rehypeRaw]}
                components={{
                  img: ({ src, alt, ...props }) => {
                    return (
                      <img
                        src={src}
                        alt={alt || 'Image'}
                        style={{
                          maxWidth: '100%',
                          height: 'auto',
                          borderRadius: '4px',
                          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                          display: 'block',
                          margin: '10px 0',
                        }}
                        onError={(e) => {
                          // Fallback to show the alt text or filename
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                          const fallback = document.createElement('div');
                          fallback.textContent = alt || 'Image failed to load';
                          fallback.style.cssText = `
                            padding: 20px;
                            background: #f5f5f5;
                            border: 1px solid #ddd;
                            border-radius: 4px;
                            text-align: center;
                            color: #666;
                            font-style: italic;
                          `;
                          target.parentNode?.insertBefore(fallback, target.nextSibling);
                        }}
                        {...props}
                      />
                    );
                  },
                }}
              >
                {formData.content}
              </ReactMarkdown>
            </Box>
          </Box>
        )}

        {/* Show error message instead of preview when dangerous content is detected */}
        {formData.content && hasDangerous && (
          <Box data-testid='help-edit-dangerous-content-warning'>
            <Alert severity='error' sx={{ mb: 1 }}>
              <Typography variant='body2' fontWeight='bold' sx={{ mb: 1 }}>
                {t('help.dangerousContentDetected', 'Dangerous content detected')}
              </Typography>
              <Typography variant='body2'>
                {t(
                  'help.dangerousContentMessage',
                  'Your content contains unsafe elements (scripts, dangerous HTML, etc.). Preview is disabled for security reasons. The content will be automatically sanitized when you save. Please remove any script tags, javascript: protocols, or other dangerous content.'
                )}
              </Typography>
            </Alert>
          </Box>
        )}
      </Box>
    </Modal>
  );
};

export default HelpEditModal;